import { Elastic, Sine, TimelineLite, TimelineMax } from "gsap";
import * as HLF from "html-living-framework";
import { AppProgress } from "../Data/AppProgress";
import * as Constants from "../Data/Constants";
import { GalleryPaintableData } from "../Data/GalleryPaintableData";
import { GameType } from "../Data/GameData";
import * as MaskData from "../Data/MaskData";
import * as VoiceOver from "../Data/VoiceOver";
import { PaintableMask } from "../GameObjects/PaintableMask";
import { GamePaintable } from "../States/GamePaintable";

export class GameMask extends GamePaintable {
    protected readonly _gameType = GameType.MASK;
    protected readonly _colours: number[] = [
        // Day of the Dead
        (new HLF.Colour(255, 245, 91)).toHex(), // Yellow
        (new HLF.Colour(255, 167, 50)).toHex(), // Orange
        (new HLF.Colour(255, 98, 81)).toHex(), // Red
        (new HLF.Colour(234, 125, 235)).toHex(), // Pink
        (new HLF.Colour(164, 91, 255)).toHex(), // Purple
        (new HLF.Colour(174, 255, 109)).toHex(), // Green
        (new HLF.Colour(68, 197, 239)).toHex(), // Cyan
        (new HLF.Colour(70, 22, 217)).toHex(), // Blue
        (new HLF.Colour(242, 238, 226)).toHex(), // Bone
        (new HLF.Colour(20, 25, 20)).toHex(), // Black
        // Chinese Opera
        (new HLF.Colour(223, 207, 48)).toHex(), // Yellow
        (new HLF.Colour(228, 133, 53)).toHex(), // Orange
        (new HLF.Colour(227, 135, 150)).toHex(), // Pink
        (new HLF.Colour(232, 32, 32)).toHex(), // Red
        (new HLF.Colour(56, 191, 88)).toHex(), // Green
        (new HLF.Colour(42, 115, 226)).toHex(), // Blue
        (new HLF.Colour(255, 255, 255)).toHex(), // White
        (new HLF.Colour(0, 0, 0)).toHex(), // Black
    ];
    protected _swapHintTween: TimelineMax | undefined;

    constructor() {
        super();
        this._funFactCommon = VoiceOver.FUN_FACT_MASK_COMMON;
        this._funFactUnique = VoiceOver.FUN_FACT_MASK_UNIQUE;

        this._palette.setColours(this._colours);

        for (let i = 0; i < MaskData.LAYER_DATA.length; ++i) {
            this._paintables.push(new PaintableMask(i));
            const data: GalleryPaintableData = AppProgress.games[this._gameType].saveData[i] as GalleryPaintableData;
            if (data !== undefined) {
                for (let j = 0; j < data.sequence.length; ++j) {
                    this._paintables[i].layers[data.sequence[j]].paint(
                        data.colours[data.sequence[j]],
                        0, 0, true, true,
                    );
                }
            }
            this.addChild(this._paintables[i]);
            this.setChildIndex(this._paintables[i], 4);
        }

        HLF.PixiUtils.sendToFront(this._andy);
        HLF.PixiUtils.sendToFront(this._buttonSubmit);
        HLF.PixiUtils.sendToFront(this._buttonClear);
        HLF.PixiUtils.sendToFront(this._onboardingManager);
        HLF.PixiUtils.sendToFront(this._zoomOnboarding);
        HLF.PixiUtils.sendToFront(this._keyboardFocusManager);
    }

    public static getRequiredAssets(): string[] {
        const required = super.getRequiredAssets();
        for (let i = 0; i < MaskData.LAYER_DATA.length; ++i) {
            required.push(Constants.SPRITESHEET_MASK_PREFIX + "0" + i + HLF.ResourceUtils.FILE_EXTENSION_JSON);
        }
        return required;
    }

    public init(pSize: PIXI.Point): void {
        super.init(pSize);

        if (this._isOnboarding === false) {
            this._activePaintableIndex = HLF.Random.intBetween(0, MaskData.LAYER_DATA.length);
            this._currentFunFactIndex = Math.floor(this._activePaintableIndex / 3);
        }
        for (let i = 0; i < MaskData.LAYER_DATA.length; ++i) {
            this._paintables[i].visible = (i === this._activePaintableIndex);
        }
    }

    protected onOnboardingComplete(): void {
        super.onOnboardingComplete();
        this.playSwapHint();
    }

    protected onNextPressed(): void {
        super.onNextPressed();
        this.stopSwapHint();
    }

    protected onPrevPressed(): void {
        super.onPrevPressed();
        this.stopSwapHint();
    }

    protected incrementIndex(pStep: number): void {
        super.incrementIndex(pStep);
        this._currentFunFactIndex = Math.floor(this._activePaintableIndex / 3);
    }

    private playSwapHint(): void {
        this.stopSwapHint();

        this._swapHintTween = new TimelineMax({
            repeat: -1,
        });
        this._swapHintTween.add(this.createButtonHintTween(this._buttonNext), 5.0);
        this._swapHintTween.add(this.createButtonHintTween(this._buttonPrev), 5.2);
    }

    private createButtonHintTween(pTarget: PIXI.DisplayObject): TimelineLite {
        const timeline: TimelineLite = new TimelineLite();
        timeline.to(pTarget.scale, 0.15, {
            x: 1.15,
            y: 1.15,
            ease: Sine.easeInOut,
        });
        timeline.to(pTarget.scale, 0.1, {
            x: 0.9,
            y: 0.9,
            ease: Sine.easeOut,
        });
        timeline.to(pTarget.scale, 1.0, {
            x: 1.0,
            y: 1.0,
            ease: Elastic.easeOut,
        });
        return timeline;
    }

    private stopSwapHint(): void {
        if (this._swapHintTween !== undefined) {
            this._swapHintTween.kill();
            this._buttonNext.scale.set(1);
            this._buttonPrev.scale.set(1);
        }
    }
}
